import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Briefcase, Send, Coffee, ExternalLink, Pencil, Loader2, Sparkles } from "lucide-react";

import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * MyActivity — a single tabbed panel that surfaces everything the current
 * user owns or participates in:
 *   - My Projects     (author role) — projects they've posted, editable
 *   - My Applications (beta_reader) — applications they've submitted
 *   - Coffee Chats                  — requested + received
 *
 * All three tabs are visible when the user has both author + beta_reader
 * roles (the "Both" onboarding path). Server-side visibility already
 * scopes each endpoint to the current viewer, so this component surfaces
 * only what the user is authorised to see — no visibility rules changed.
 *
 * Reuses existing endpoints:
 *   GET /jobs?mine=true
 *   GET /jobs/applications/mine/list
 *   GET /coffee-chats
 *
 * Refetches on window focus so newly-posted projects, applications, or
 * coffee chats appear immediately without a hard refresh.
 */
const STATUS_STYLES = {
  open:        "bg-[hsl(var(--sb-forest))]/12 text-[hsl(var(--sb-forest-2))] border border-[hsl(var(--sb-forest))]/25",
  in_progress: "bg-[hsl(var(--sb-clay))]/12 text-[hsl(var(--sb-clay))] border border-[hsl(var(--sb-clay))]/25",
  completed:   "bg-muted text-muted-foreground border border-border",
  cancelled:   "bg-muted text-muted-foreground border border-border",
  closed:      "bg-muted text-muted-foreground border border-border",
  pending:     "bg-[hsl(var(--sb-gold))]/15 text-foreground border border-[hsl(var(--sb-gold))]/40",
  accepted:    "bg-[hsl(var(--sb-forest))]/12 text-[hsl(var(--sb-forest-2))] border border-[hsl(var(--sb-forest))]/25",
  declined:    "bg-destructive/10 text-destructive border border-destructive/25",
  withdrawn:   "bg-muted text-muted-foreground border border-border",
  proposed:    "bg-[hsl(var(--sb-clay))]/12 text-[hsl(var(--sb-clay))] border border-[hsl(var(--sb-clay))]/25",
};

const humanStatus = (s) =>
  (s || "").replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase()) || "—";

function StatusChip({ status }) {
  const cls = STATUS_STYLES[status] || "bg-muted text-muted-foreground border border-border";
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] ${cls}`}>
      {humanStatus(status)}
    </span>
  );
}

function EmptyState({ icon: Icon, title, body, cta, to }) {
  return (
    <div className="text-center py-10 sm:py-12 border border-dashed rounded-2xl">
      <div className="mx-auto w-12 h-12 rounded-full bg-[hsl(var(--sb-moss))] grid place-items-center text-[hsl(var(--sb-forest-2))]">
        <Icon size={20} />
      </div>
      <h4 className="mt-4 font-serif text-lg">{title}</h4>
      <p className="mt-1 text-sm text-muted-foreground max-w-sm mx-auto leading-relaxed">{body}</p>
      {cta && to && (
        <Link to={to} className="btn-secondary inline-flex mt-4 text-xs">
          {cta}
        </Link>
      )}
    </div>
  );
}

function LoadingBlock() {
  return (
    <div className="flex items-center justify-center py-10 text-muted-foreground">
      <Loader2 size={18} className="animate-spin" />
    </div>
  );
}

function MyProjectsList({ user }) {
  const [rows, setRows] = useState(null);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/jobs", { params: { mine: true } });
      // Show newest first — /jobs already returns author-scoped list.
      const sorted = [...(data || [])].sort((a, b) => {
        const da = new Date(a.created_at || 0).getTime();
        const db = new Date(b.created_at || 0).getTime();
        return db - da;
      });
      setRows(sorted);
    } catch {
      setRows([]);
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [load]);

  if (rows === null) return <LoadingBlock />;
  if (rows.length === 0) {
    return (
      <EmptyState
        icon={Briefcase}
        title="No projects yet"
        body="When you post a project it'll appear here so you can open, edit, or manage it in one place."
        cta="Post a project"
        to="/jobs/new"
      />
    );
  }
  return (
    <ul className="space-y-2" data-testid="my-activity-projects-list">
      {rows.map((j) => (
        <li
          key={j.id}
          className="flex items-center gap-3 rounded-2xl border bg-card p-3 sm:p-4"
          data-testid={`my-activity-project-row-${j.id}`}
        >
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <StatusChip status={j.status} />
              {j.listing_type === "demo" && (
                <span className="text-[10px] uppercase tracking-[0.14em] font-semibold text-[hsl(var(--sb-clay))]">
                  Demo / Test
                </span>
              )}
              <span className="text-[10px] text-muted-foreground">
                {j.applications_count ?? 0} {(j.applications_count ?? 0) === 1 ? "application" : "applications"}
              </span>
            </div>
            <Link
              to={`/jobs/${j.id}`}
              className="mt-1 block font-medium truncate text-sm sm:text-base hover:underline"
              data-testid={`my-activity-project-title-${j.id}`}
            >
              {j.title}
            </Link>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <Link
              to={`/jobs/${j.id}/edit`}
              className="inline-flex items-center gap-1 rounded-full border border-border px-2.5 py-1.5 text-xs hover:bg-muted"
              data-testid={`my-activity-project-edit-${j.id}`}
              title="Edit project"
            >
              <Pencil size={12} /> Edit
            </Link>
            <Link
              to={`/jobs/${j.id}`}
              className="inline-flex items-center gap-1 rounded-full border border-border px-2.5 py-1.5 text-xs hover:bg-muted"
              data-testid={`my-activity-project-open-${j.id}`}
              title="Open project"
            >
              <ExternalLink size={12} /> Open
            </Link>
          </div>
        </li>
      ))}
    </ul>
  );
}

function MyApplicationsList() {
  const [rows, setRows] = useState(null);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/jobs/applications/mine/list");
      setRows(data || []);
    } catch {
      setRows([]);
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [load]);

  if (rows === null) return <LoadingBlock />;
  if (rows.length === 0) {
    return (
      <EmptyState
        icon={Send}
        title="No applications yet"
        body="Applications you submit will appear here with their status."
        cta="Browse projects"
        to="/jobs"
      />
    );
  }
  return (
    <ul className="space-y-2" data-testid="my-activity-applications-list">
      {rows.map(({ application, job }) => (
        <li
          key={application.id}
          className="rounded-2xl border bg-card p-3 sm:p-4"
          data-testid={`my-activity-application-row-${application.id}`}
        >
          <div className="flex items-center gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <StatusChip status={application.status} />
                <span className="text-[10px] text-muted-foreground">
                  Applied {new Date(application.created_at).toLocaleDateString()}
                </span>
              </div>
              <Link
                to={job ? `/jobs/${job.id}` : "#"}
                className="mt-1 block font-medium truncate text-sm sm:text-base hover:underline"
                data-testid={`my-activity-application-job-${application.id}`}
              >
                {job?.title || "Project no longer available"}
              </Link>
            </div>
            {job && (
              <Link
                to={`/jobs/${job.id}`}
                className="shrink-0 inline-flex items-center gap-1 rounded-full border border-border px-2.5 py-1.5 text-xs hover:bg-muted"
                data-testid={`my-activity-application-open-${application.id}`}
              >
                <ExternalLink size={12} /> View
              </Link>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}

function MyCoffeeChatsList({ user }) {
  const [rows, setRows] = useState(null);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/coffee-chats");
      setRows(data || []);
    } catch {
      setRows([]);
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [load]);

  if (rows === null) return <LoadingBlock />;
  if (rows.length === 0) {
    return (
      <EmptyState
        icon={Coffee}
        title="No Coffee Chats yet"
        body="Coffee Chats you request or receive will appear here."
        cta="Open Coffee Chats"
        to="/coffee-chats"
      />
    );
  }
  return (
    <ul className="space-y-2" data-testid="my-activity-coffee-chats-list">
      {rows.map((c) => {
        const isRequester = c.requester_id === user?.id;
        return (
          <li
            key={c.id}
            className="rounded-2xl border bg-card p-3 sm:p-4"
            data-testid={`my-activity-coffee-row-${c.id}`}
          >
            <div className="flex items-center gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <StatusChip status={c.status} />
                  <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
                    {isRequester ? "Requested by you" : "Received"}
                  </span>
                </div>
                {c.message && (
                  <p className="mt-1 text-sm truncate text-foreground/85">{c.message}</p>
                )}
              </div>
              {c.thread_id ? (
                <Link
                  to={`/messages/${c.thread_id}`}
                  className="shrink-0 inline-flex items-center gap-1 rounded-full border border-border px-2.5 py-1.5 text-xs hover:bg-muted"
                  data-testid={`my-activity-coffee-open-${c.id}`}
                >
                  <ExternalLink size={12} /> Open chat
                </Link>
              ) : (
                <Link
                  to="/coffee-chats"
                  className="shrink-0 inline-flex items-center gap-1 rounded-full border border-border px-2.5 py-1.5 text-xs hover:bg-muted"
                  data-testid={`my-activity-coffee-manage-${c.id}`}
                >
                  Manage
                </Link>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

export default function MyActivity() {
  const { user } = useAuth();
  const roles = user?.roles || [];
  const isAuthor = roles.includes("author");
  const isReader = roles.includes("beta_reader");

  const tabs = useMemo(() => {
    const t = [];
    if (isAuthor) t.push({ id: "projects", label: "My Projects", icon: Briefcase });
    if (isReader) t.push({ id: "applications", label: "My Applications", icon: Send });
    t.push({ id: "coffee-chats", label: "Coffee Chats", icon: Coffee });
    return t;
  }, [isAuthor, isReader]);

  const [active, setActive] = useState(tabs[0]?.id || "coffee-chats");
  // Keep `active` in sync if the user's roles change (e.g. add role).
  useEffect(() => {
    if (!tabs.find((t) => t.id === active)) setActive(tabs[0]?.id);
  }, [tabs, active]);

  if (!user || tabs.length === 0) return null;

  return (
    <section
      id="my-activity"
      className="card-soft mt-10 scroll-mt-24"
      data-testid="dashboard-my-activity"
      aria-labelledby="my-activity-heading"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.16em] text-[hsl(var(--sb-forest-2))]">
            <Sparkles size={12} /> Your workspace
          </div>
          <h2 id="my-activity-heading" className="mt-1 font-serif text-2xl">My Activity</h2>
          <p className="mt-1 text-sm text-muted-foreground max-w-xl leading-relaxed">
            Everything you've posted, applied to, or connected on — all in one place.
          </p>
        </div>
      </div>

      <div
        className="mt-5 -mx-1 overflow-x-auto"
        role="tablist"
        aria-label="My Activity tabs"
      >
        <div className="inline-flex gap-1 px-1 min-w-full sm:min-w-0">
          {tabs.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={active === id}
              onClick={() => setActive(id)}
              data-testid={`my-activity-tab-${id}`}
              className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs sm:text-sm font-medium transition-colors ${
                active === id
                  ? "bg-[hsl(var(--sb-forest))] text-white"
                  : "bg-muted/60 text-foreground/70 hover:bg-muted"
              }`}
            >
              <Icon size={14} /> {label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-5">
        {active === "projects" && <MyProjectsList user={user} />}
        {active === "applications" && <MyApplicationsList />}
        {active === "coffee-chats" && <MyCoffeeChatsList user={user} />}
      </div>
    </section>
  );
}
